import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

const ITEM_H  = 36;

const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00','05','10','15','20','25','30','35','40','45','50','55'];

/* ─── Helpers ─────────────────────────────────────── */
const toMin  = (s) => { const [h, m] = s.split(':').map(Number); return h * 60 + (m || 0); };
const parseT = (s) => { if (!s) return null; const [h, m] = s.split(':').map(Number); return { h, m }; };

const roundMIdx = (m) => Math.max(0, Math.min(11, Math.round(m / 5)));

const outOfRange = (v, min, max) =>
  v && ((min && toMin(v) < toMin(min)) || (max && toMin(v) > toMin(max)));

/* ─── TimePicker ──────────────────────────────────── */
const TimePicker = ({ value, onChange, minTime, maxTime }) => {
  const [open,   setOpen]   = useState(false);
  const [draftH, setDraftH] = useState(8);
  const [draftM, setDraftM] = useState(0);
  const [pos,    setPos]    = useState({ top: 0, left: 0 });

  const triggerRef   = useRef(null);
  const popoverRef   = useRef(null);
  const hColRef      = useRef(null);
  const mColRef      = useRef(null);
  const hScrollTimer = useRef(null);
  const mScrollTimer = useRef(null);

  /* cleanup timers on unmount */
  useEffect(() => () => {
    clearTimeout(hScrollTimer.current);
    clearTimeout(mScrollTimer.current);
  }, []);

  /* ── Parsed constraints ── */
  const minT = useMemo(() => parseT(minTime), [minTime]);
  const maxT = useMemo(() => parseT(maxTime), [maxTime]);

  /* ── Disabled predicates ── */
  const isHourDisabled = useCallback((i) => {
    if (minT && i < minT.h) return true;
    if (maxT && i > maxT.h) return true;
    return false;
  }, [minT, maxT]);

  const isMinDisabled = useCallback((mIdx, h) => {
    const v = parseInt(MINUTES[mIdx]);
    if (minT && h === minT.h && v < minT.m) return true;
    if (maxT && h === maxT.h && v > maxT.m) return true;
    return false;
  }, [minT, maxT]);

  /* ── Nearest enabled index helpers ── */
  const nearestEnabledH = useCallback((raw) => {
    if (!isHourDisabled(raw)) return raw;
    for (let d = 1; d <= 23; d++) {
      if (raw - d >= 0  && !isHourDisabled(raw - d)) return raw - d;
      if (raw + d <= 23 && !isHourDisabled(raw + d)) return raw + d;
    }
    return raw;
  }, [isHourDisabled]);

  const nearestEnabledM = useCallback((mIdx, h) => {
    if (!isMinDisabled(mIdx, h)) return mIdx;
    for (let d = 1; d <= 11; d++) {
      if (mIdx - d >= 0  && !isMinDisabled(mIdx - d, h)) return mIdx - d;
      if (mIdx + d <= 11 && !isMinDisabled(mIdx + d, h)) return mIdx + d;
    }
    return mIdx;
  }, [isMinDisabled]);

  /* ── Clamp draft into valid range ── */
  const clampH = useCallback((i) => {
    let v = i;
    if (minT && v < minT.h) v = minT.h;
    if (maxT && v > maxT.h) v = maxT.h;
    return v;
  }, [minT, maxT]);

  const clampM = useCallback((mIdx, h) => {
    if (minT && h === minT.h) {
      const first = MINUTES.findIndex((m) => parseInt(m) >= minT.m);
      if (first >= 0 && mIdx < first) return first;
    }
    if (maxT && h === maxT.h) {
      const last = MINUTES.reduce((acc, m, i) => parseInt(m) <= maxT.m ? i : acc, -1);
      if (last >= 0 && mIdx > last) return last;
    }
    return mIdx;
  }, [minT, maxT]);

  /* ── Smooth-scroll column to an index ── */
  const scrollH = useCallback((i, smooth = true) => {
    hColRef.current?.scrollTo({ top: i * ITEM_H, behavior: smooth ? 'smooth' : 'instant' });
  }, []);
  const scrollM = useCallback((i, smooth = true) => {
    mColRef.current?.scrollTo({ top: i * ITEM_H, behavior: smooth ? 'smooth' : 'instant' });
  }, []);

  /* ── Open ── */
  const handleOpen = useCallback(() => {
    let hIdx = 8, mIdx = 0;
    if (value) {
      const [h, m] = value.split(':').map(Number);
      hIdx = h; mIdx = roundMIdx(m);
    }
    hIdx = clampH(hIdx);
    mIdx = clampM(mIdx, hIdx);
    setDraftH(hIdx);
    setDraftM(mIdx);

    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 6, left: r.left + window.scrollX });

    setOpen(true);
  }, [value, clampH, clampM]);

  /* Scroll to initial position immediately after mount */
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      scrollH(draftH, false);
      scrollM(draftM, false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── Scroll handlers — read center item after scroll settles ── */
  const handleHScroll = useCallback(() => {
    clearTimeout(hScrollTimer.current);
    hScrollTimer.current = setTimeout(() => {
      if (!hColRef.current) return;
      const raw = Math.round(hColRef.current.scrollTop / ITEM_H);
      let idx = Math.max(0, Math.min(23, raw));
      if (isHourDisabled(idx)) {
        idx = nearestEnabledH(idx);
        scrollH(idx, true);
      }
      setDraftH(idx);
      setDraftM((prev) => {
        const next = clampM(prev, idx);
        if (next !== prev) scrollM(next, true);
        return next;
      });
    }, 120);
  }, [isHourDisabled, nearestEnabledH, clampM, scrollH, scrollM]);

  const handleMScroll = useCallback(() => {
    clearTimeout(mScrollTimer.current);
    mScrollTimer.current = setTimeout(() => {
      if (!mColRef.current) return;
      const raw = Math.round(mColRef.current.scrollTop / ITEM_H);
      let idx = Math.max(0, Math.min(11, raw));
      if (isMinDisabled(idx, draftH)) {
        idx = nearestEnabledM(idx, draftH);
        scrollM(idx, true);
      }
      setDraftM(idx);
    }, 120);
  }, [isMinDisabled, draftH, nearestEnabledM, scrollM]);

  /* ── Outside click → close ── */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!popoverRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  /* ── Confirm: commit whichever items are centered ── */
  const handleConfirm = () => {
    onChange(HOURS[draftH] + ':' + MINUTES[draftM]);
    setOpen(false);
  };

  /* ── Click item → scroll to it ── */
  const handleHClick = (i) => {
    if (isHourDisabled(i)) return;
    scrollH(i);
  };
  const handleMClick = (i) => {
    if (isMinDisabled(i, draftH)) return;
    scrollM(i);
  };

  /* ── Keyboard ── */
  const nextH = (from, dir) => {
    let n = from + dir;
    while (n >= 0 && n <= 23) { if (!isHourDisabled(n)) return n; n += dir; }
    return from;
  };
  const nextM = (from, dir) => {
    let n = from + dir;
    while (n >= 0 && n <= 11) { if (!isMinDisabled(n, draftH)) return n; n += dir; }
    return from;
  };

  const hKeyDown = (e) => {
    if (e.key === 'ArrowUp')   { e.preventDefault(); const i = nextH(draftH, -1); scrollH(i); setDraftH(i); }
    if (e.key === 'ArrowDown') { e.preventDefault(); const i = nextH(draftH, +1); scrollH(i); setDraftH(i); }
    if (e.key === 'Enter')     handleConfirm();
    if (e.key === 'Escape')    setOpen(false);
  };
  const mKeyDown = (e) => {
    if (e.key === 'ArrowUp')   { e.preventDefault(); const i = nextM(draftM, -1); scrollM(i); setDraftM(i); }
    if (e.key === 'ArrowDown') { e.preventDefault(); const i = nextM(draftM, +1); scrollM(i); setDraftM(i); }
    if (e.key === 'Enter')     handleConfirm();
    if (e.key === 'Escape')    setOpen(false);
  };

  const warn = outOfRange(value, minTime, maxTime);

  return (
    <>
      {/* ── Trigger ── */}
      <button
        ref={triggerRef}
        type="button"
        className={`tp-trigger${warn ? ' tp-trigger--warn' : ''}`}
        aria-expanded={open}
        onClick={handleOpen}
      >
        <span className="tp-trigger-time">{value || '--:--'}</span>
        {warn
          ? <span style={{ fontSize: '0.7rem' }}>⚠</span>
          : <span className="tp-trigger-chevron">▾</span>
        }
      </button>

      {/* ── Popover (portal) ── */}
      {open && createPortal(
        <div
          ref={popoverRef}
          className="tp-popover"
          style={{ position: 'absolute', top: pos.top, left: pos.left }}
        >
          {/* Column labels */}
          <div className="tp-cols-header">
            <span className="tp-col-label">Hora</span>
            <span className="tp-col-label">Min</span>
          </div>

          {/* Drum columns */}
          <div className="tp-drums">
            {/* Center selection band */}
            <div className="tp-sel-band" aria-hidden="true" />

            {/* Hours */}
            <div
              className="tp-col"
              ref={hColRef}
              tabIndex={0}
              onScroll={handleHScroll}
              onKeyDown={hKeyDown}
            >
              <div className="tp-pad" />
              {HOURS.map((h, i) => (
                <button
                  key={h}
                  type="button"
                  className={`tp-item${isHourDisabled(i) ? ' tp-item--dis' : ''}`}
                  onClick={() => handleHClick(i)}
                >
                  {h}
                </button>
              ))}
              <div className="tp-pad" />
            </div>

            <span className="tp-colon">:</span>

            {/* Minutes */}
            <div
              className="tp-col"
              ref={mColRef}
              tabIndex={0}
              onScroll={handleMScroll}
              onKeyDown={mKeyDown}
            >
              <div className="tp-pad" />
              {MINUTES.map((m, i) => {
                const dis = isMinDisabled(i, draftH);
                return (
                  <button
                    key={m}
                    type="button"
                    className={`tp-item${dis ? ' tp-item--dis' : ''}`}
                    onClick={() => handleMClick(i)}
                  >
                    {m}
                  </button>
                );
              })}
              <div className="tp-pad" />
            </div>
          </div>

          <button type="button" className="tp-confirm" onClick={handleConfirm}>
            Confirmar
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default TimePicker;
